import { Box, Link, Paper, Stack, Typography } from "@mui/material";
import GoHomeButton from "./GoHomeButton";

const LAST_UPDATED = "August 30, 2026";

export default function TermsOfService({ onNavigateHome, onNavigate = () => {}, homeLabel = "Go Home" }) {
  return (
    <Paper elevation={2} className="page-card" sx={{ p: 3, mt: 3, mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" className="section-header">Terms of Service</Typography>
        <GoHomeButton onNavigateHome={onNavigateHome} label={homeLabel} />
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">Last updated: {LAST_UPDATED}</Typography>

        <Typography variant="body2" color="text.secondary">
          Wishlist Manager (the "Service") is operated by LostNSound Enterprises ("LostNSound," "we,"
          "us," or "our"). These Terms of Service ("Terms") govern your access to and use of the
          Service at wishlistmgr.com. Please read them carefully. Our Privacy Policy explains how we
          handle your information and is incorporated into these Terms by reference.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>1. Acceptance of Terms.</Typography>
        <Typography variant="body2" color="text.secondary">
          By creating an account or otherwise using the Service, you agree to be bound by these Terms
          and by our Privacy Policy. If you do not agree, do not use the Service.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>2. Eligibility.</Typography>
        <Typography variant="body2" color="text.secondary">
          You must be at least 18 years old, or the age of majority in your jurisdiction if higher, to
          create an account or use the Service. The Service is intended for adults coordinating gifts
          within their family or friend groups. A person who cannot manage their own account —
          including a child — may be represented on the Service only as a "list member" added and
          managed by an adult account holder who is their parent, legal guardian, or caregiver. List
          members do not have accounts, credentials, or independent access to the Service, and we do
          not collect information directly from them. By adding a list member, you confirm you have
          the authority to provide the information you enter about that person.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>3. Accounts.</Typography>
        <Typography variant="body2" color="text.secondary">
          You are responsible for maintaining the confidentiality of your login credentials and for
          all activity that occurs under your account. You agree to provide accurate information when
          you register and to keep it current. You may not share your account, create an account for
          anyone else (other than adding a managed list member as described above), or use another
          person's account without permission. Notify us promptly at support@wishlistmgr.com if you
          believe your account has been compromised. You may delete your account at any time from the
          Settings screen.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>4. The Service.</Typography>
        <Typography variant="body2" color="text.secondary">
          Wishlist Manager lets you create wishlists, form or join family and friend "Groups," invite
          others to your Group, add items to your own list, and mark items on other members' lists as
          "claimed" so that gift-givers can coordinate without duplicating gifts or revealing
          purchases to the list owner. The Service also offers optional features such as importing
          items from a public Amazon wishlist URL, uploading item photos, ranking items by priority,
          "surprise" items, Group Leader tools (including countdown dates and an optional wishlist
          edit lock), CSV export, and an in-app messaging and feedback inbox. The Service currently
          supports a single "Christmas Gift" mode; we may add other occasion modes in the future. We
          may change, add, or remove features at any time.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>5. User Content.</Typography>
        <Typography variant="body2" color="text.secondary">
          "User Content" means anything you submit to the Service, including list and member names,
          item descriptions, links, prices, notes, photos, feedback messages, and Group information.
          You retain all ownership rights in your User Content. You grant LostNSound a non-exclusive,
          worldwide, royalty-free license to host, store, reproduce, modify (for example, to resize
          an image), display, and transmit your User Content solely to operate, provide, secure, and
          improve the Service for you and the Groups you belong to. This license ends when your User
          Content is deleted from the Service, except for residual copies retained in routine backups
          for a limited period and for content that has been shared with other members and retained
          as part of their experience (for example, a claim recorded on another member's list). You
          are solely responsible for your User Content and represent that: (a) you own it or have the
          necessary rights to submit it; (b) it does not infringe or violate the rights of any third
          party, including intellectual property and privacy rights; and (c) it does not violate
          these Terms or any applicable law. We may remove User Content that we reasonably believe
          violates these Terms or may harm the Service or others.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>6. Acceptable Use.</Typography>
        <Typography variant="body2" color="text.secondary">You agree not to:</Typography>
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              use the Service for any unlawful, harmful, fraudulent, or deceptive purpose;
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              attempt to access accounts, Groups, data, or systems that you are not authorized to
              access, or probe, scan, or test the vulnerability of the Service;
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              upload or transmit viruses, malware, or other harmful code, or otherwise interfere with
              or disrupt the integrity or performance of the Service;
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              use any robot, scraper, or automated means to access the Service or extract data from
              it, except as expressly permitted by us;
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              use the Service to harass, abuse, defame, threaten, defraud, or harm any person,
              including through misuse of the claiming, messaging, or invitation features;
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              upload content that is obscene, hateful, or that depicts or exploits minors
              inappropriately;
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              send unsolicited or bulk messages, or invite people to a Group who have not agreed to
              receive an invitation from you;
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              misrepresent your identity or your affiliation with any person or entity; or
            </Typography>
          </li>
          <li>
            <Typography variant="body2" component="span" color="text.secondary">
              resell, sublicense, or commercially exploit the Service or its content without our
              permission.
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" sx={{ mt: 2 }}>7. Group Invitations and Email.</Typography>
        <Typography variant="body2" color="text.secondary">
          If you invite another person to a Group by entering their email address, you confirm that
          you know that person and have a good-faith basis to believe they would welcome the
          invitation. We will send that address a one-time invitation email on your behalf. We are
          not responsible for invitations sent in error or for addresses entered incorrectly.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>8. Third-Party Services, Links, and Retailers.</Typography>
        <Typography variant="body2" color="text.secondary">
          The Service relies on third-party providers (including Supabase, Cloudflare, Resend, and, if
          you choose Google sign-in, Google) to function. Your use of those integrated features may
          also be subject to the third parties' own terms. Wishlist items may contain links to
          third-party retailers, including Amazon. If you use the Amazon import feature, you are
          responsible for providing a URL you are authorized to access, and your use of Amazon's
          website remains subject to Amazon's terms. We are not affiliated with, and do not endorse,
          any third-party retailer. We do not control and are not responsible for the products,
          prices, availability, payment, shipping, returns, or customer service of any third-party
          retailer, or for the content or practices of any linked website.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>9. Affiliate Disclosure.</Typography>
        <Typography variant="body2" color="text.secondary">
          We may in the future participate in retailer affiliate programs (such as the Amazon
          Associates Program). If and when we do, some retailer links on the Service may be affiliate
          links, which means we may earn a commission on qualifying purchases made through them at no
          additional cost to you. Consistent with U.S. Federal Trade Commission guidance, we will
          disclose that relationship here and, where practicable, at or near the link itself. As of
          the "last updated" date above, no affiliate program is active on the Service.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>10. Paid Features.</Typography>
        <Typography variant="body2" color="text.secondary">
          The Service is currently free to use. We may in the future offer optional, paid features
          (for example, a one-time premium unlock or a voluntary contribution). If we do, the price
          and what is included will be disclosed to you before you pay, payment will be processed by
          a third-party payment processor, and all sales will be final except where a refund is
          required by applicable law.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>11. Intellectual Property.</Typography>
        <Typography variant="body2" color="text.secondary">
          The Service — including its software, design, text, graphics, logos, and the "Wishlist
          Manager" name — is owned by LostNSound or its licensors and is protected by intellectual
          property laws. Subject to these Terms, we grant you a limited, non-exclusive,
          non-transferable, revocable license to access and use the Service for your personal,
          non-commercial gift-coordination purposes. You may not copy, modify, distribute, reverse
          engineer, or create derivative works of the Service except as permitted by law.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>12. Feedback.</Typography>
        <Typography variant="body2" color="text.secondary">
          If you send us feedback or suggestions, you grant us a perpetual, irrevocable, royalty-free
          license to use them for any purpose without obligation or compensation to you.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>13. Disclaimers.</Typography>
        <Typography variant="body2" color="text.secondary">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS, IMPLIED, OR STATUTORY, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. We do not warrant that the Service
          will be uninterrupted, timely, secure, or error-free, that defects will be corrected, or
          that the Service is free of harmful components. We do not guarantee that gifts will not be
          duplicated or that a surprise will not be revealed, including as a result of user error,
          another member's actions, a third-party retailer, or a technical issue. The Service is
          early-stage software and is under active development. Some jurisdictions do not allow the
          exclusion of certain warranties, so some of the above may not apply to you.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>14. Limitation of Liability.</Typography>
        <Typography variant="body2" color="text.secondary">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, LOSTNSOUND AND ITS OWNERS, OPERATORS, AND SUPPLIERS
          WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
          PUNITIVE DAMAGES, OR FOR ANY LOSS OF DATA, PROFITS, GOODWILL, OR THE COST OF A SUBSTITUTE
          SERVICE, ARISING OUT OF OR RELATING TO YOUR USE OF (OR INABILITY TO USE) THE SERVICE,
          WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, EVEN IF WE HAVE BEEN
          ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR
          TOTAL LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A)
          THE TOTAL AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE LIABILITY,
          OR (B) USD $50. Some jurisdictions do not allow certain limitations, so some of the above
          may not apply to you.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>15. Indemnification.</Typography>
        <Typography variant="body2" color="text.secondary">
          You agree to indemnify and hold harmless LostNSound and its owners and operators from any
          claim, demand, loss, or expense (including reasonable attorneys' fees) arising out of your
          User Content, your use of the Service, your violation of these Terms, or your violation of
          any law or the rights of a third party.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>16. Suspension and Termination.</Typography>
        <Typography variant="body2" color="text.secondary">
          We may suspend or terminate your access to the Service at any time if you violate these
          Terms, if we reasonably believe your use creates a risk or legal exposure for us or another
          person, or if we discontinue the Service. You may stop using the Service and delete your
          account at any time. Sections that by their nature should survive termination (including
          Section 5 as to the license for retained or shared content, Sections 9–15, and Sections
          17–19) will survive.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>17. Changes to These Terms.</Typography>
        <Typography variant="body2" color="text.secondary">
          We may update these Terms from time to time. If we make a material change, we will provide
          notice through the Service or by email before the change takes effect, and we will update
          the "last updated" date. Your continued use of the Service after the changes take effect
          means you accept the updated Terms.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>18. Governing Law and Disputes.</Typography>
        <Typography variant="body2" color="text.secondary">
          These Terms are governed by the laws of the State of North Carolina, United States, without
          regard to its conflict-of-laws rules. You and LostNSound agree that any dispute arising out
          of or relating to these Terms or the Service that is not resolved informally will be brought
          exclusively in the state or federal courts located in North Carolina, and you consent to
          personal jurisdiction there. Before filing any claim, you agree to first contact us at
          support@wishlistmgr.com and attempt in good faith to resolve the dispute for at least 30
          days. Nothing in this section prevents either party from seeking relief in a small-claims
          court for qualifying claims.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>19. Miscellaneous.</Typography>
        <Typography variant="body2" color="text.secondary">
          These Terms, together with the Privacy Policy, are the entire agreement between you and
          LostNSound regarding the Service and supersede any prior agreements. If any provision is
          found unenforceable, the remaining provisions will remain in effect. Our failure to enforce
          a provision is not a waiver of it. You may not assign these Terms without our consent; we
          may assign them to a successor or affiliate, including in connection with forming a company
          to operate the Service or a merger, acquisition, or sale of assets. Neither party is liable
          for delays or failures caused by events beyond its reasonable control.
        </Typography>

        <Typography variant="h6" sx={{ mt: 2 }}>20. Contact.</Typography>
        <Typography variant="body2" color="text.secondary">
          Questions about these Terms: support@wishlistmgr.com, or use the Send Feedback box on the
          Messages screen.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2 }}>
          See also:{" "}
          <Link component="button" variant="body2" onClick={() => onNavigate("privacy")}>
            Privacy Policy
          </Link>
        </Typography>
      </Stack>
    </Paper>
  );
}
